import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type {
    ClienteDTO,
    PagaebleResult,
    SearchSiniestroDTO,
    SiniestroDTO,
    SiniestroStatus,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import {
    CLAIMS_PATH,
    claimStatusView,
    CLOSED_STATUS,
    editClaimLocation,
    errorMessage,
    formatCompactDate,
    NEW_CLAIM_PATH,
    pageWindow,
    personName,
    resolutionView,
} from '../common/claim-common'
import type { ClaimLocationState, ClaimStatusChip, ResolutionView } from '../common/claim-common'

export const PAGE_SIZES = [20, 50, 100]
const TEXT_DELAY_MS = 400
const LOAD_ERROR = 'No se pudo cargar el listado de siniestros.'

export interface ClaimListFilters {
    cliente: ClienteDTO | null
    noSiniestro: string
    noPoliza: string
    status: SiniestroStatus | ''
    ramo: string
    showClosed: boolean
}

export type ClaimTextFilter = 'noSiniestro' | 'noPoliza'

export interface ClaimRow {
    id: number
    noSiniestro: string | null
    ramo: string | null
    name: string
    noPoliza: string | null
    aseguradora: string | null
    fechaSiniestro: string
    resolucion: ResolutionView
    status: ClaimStatusChip
}

const defaultFilters: ClaimListFilters = {
    cliente: null,
    noSiniestro: '',
    noPoliza: '',
    status: '',
    ramo: '',
    showClosed: false,
}

export const NEW_CLAIM_LOCATION: { pathname: string; state: ClaimLocationState } = {
    pathname: NEW_CLAIM_PATH,
    state: { backUrl: CLAIMS_PATH },
}

const buildQuery = (filters: ClaimListFilters) => {
    const params: string[] = ['order=fechaEstimadaResolucion%20asc']
    if (filters.cliente?.id) params.push(`clienteId=${filters.cliente.id}`)
    if (filters.noSiniestro.trim()) params.push(`noSiniestro[cn]=${encodeURIComponent(filters.noSiniestro.trim())}`)
    if (filters.noPoliza.trim()) params.push(`noPoliza[cn]=${encodeURIComponent(filters.noPoliza.trim())}`)
    if (filters.ramo) params.push(`ramo=${encodeURIComponent(filters.ramo)}`)
    // `[ni]` compara contra el nombre del enum, que el API define en mayúsculas.
    if (filters.status) params.push(`status=${filters.status}`)
    else if (!filters.showClosed) params.push(`status[ni]=${CLOSED_STATUS.map((status) => status.toUpperCase()).join(',')}`)
    return params.join('&')
}

const toRow = (item: SearchSiniestroDTO): ClaimRow => ({
    id: item.id,
    noSiniestro: item.noSiniestro,
    ramo: item.ramo,
    name: personName(item),
    noPoliza: item.noPoliza,
    aseguradora: item.aseguradora,
    fechaSiniestro: formatCompactDate(item.fechaSiniestro) || '—',
    resolucion: resolutionView(item.fechaEstimadaResolucion, item.status),
    status: claimStatusView(item.status),
})

export const useClaimListModel = () => {
    const history = useHistory()
    const location = useLocation()
    const filtersRef = useRef<ClaimListFilters>(defaultFilters)
    const textTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
    const [filters, setFilters] = useState<ClaimListFilters>(defaultFilters)
    const [query, setQuery] = useState(() => buildQuery(defaultFilters))
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0])
    const [reloadToken, setReloadToken] = useState(0)
    const [loading, setLoading] = useState(true)
    const [initialLoadDone, setInitialLoadDone] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [items, setItems] = useState<SearchSiniestroDTO[]>([])
    const [totalItems, setTotalItems] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [detailId, setDetailId] = useState<number | null>(() => (location.state as ClaimLocationState | null)?.detailId || null)

    useEffect(() => () => clearTimeout(textTimer.current), [])

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const response = await APIInvoke.invokeGET(`/siniestros?page=${page}&size=${pageSize}&${query}`)
                const payload: WrapperResponse<PagaebleResult<SearchSiniestroDTO>> | null = await response.json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || LOAD_ERROR)
                setItems(payload.body.items || [])
                setTotalItems(payload.body.totalItems || 0)
                setTotalPages(payload.body.totalPages || 0)
                setInitialLoadDone(true)
            } catch (loadError) {
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [page, pageSize, query, reloadToken])

    const commit = (next: ClaimListFilters) => {
        clearTimeout(textTimer.current)
        filtersRef.current = next
        setFilters(next)
        setPage(1)
        setQuery(buildQuery(next))
    }

    const commitPatch = (partial: Partial<ClaimListFilters>) => commit({ ...filtersRef.current, ...partial })

    const changeText = (key: ClaimTextFilter, value: string) => {
        const next = { ...filtersRef.current, [key]: value }
        filtersRef.current = next
        setFilters(next)
        clearTimeout(textTimer.current)
        textTimer.current = setTimeout(() => {
            setPage(1)
            setQuery(buildQuery(filtersRef.current))
        }, TEXT_DELAY_MS)
    }

    const changePageSize = (value: number) => {
        setPageSize(Number(value))
        setPage(1)
    }

    const deleted = (siniestro?: SiniestroDTO | null) => {
        const id = siniestro?.id ?? detailId
        setItems((current) => current.filter((item) => item.id !== id))
        setTotalItems((current) => Math.max(0, current - 1))
        setDetailId(null)
    }

    const rangeFrom = totalItems === 0 ? 0 : ((page - 1) * pageSize) + 1

    return {
        filters,
        page,
        pageSize,
        loading,
        initialLoadDone,
        error,
        rows: items.map(toRow),
        totalItems,
        totalPages,
        pages: totalPages > 0 ? pageWindow(page, totalPages) : [],
        rangeFrom,
        rangeTo: totalItems === 0 ? 0 : rangeFrom + items.length - 1,
        detailId,
        changeCliente: (cliente: ClienteDTO | null) => commitPatch({ cliente }),
        changeText,
        submitText: () => commit(filtersRef.current),
        changeStatus: (value: string) => commitPatch({ status: value as SiniestroStatus | '' }),
        changeRamo: (ramo: string) => commitPatch({ ramo }),
        changeShowClosed: (showClosed: boolean) => commitPatch({ showClosed }),
        clearFilters: () => commit({ ...defaultFilters }),
        setPage,
        changePageSize,
        reload: () => setReloadToken((token) => token + 1),
        openSiniestro: (id: number) => setDetailId(id),
        closeSiniestro: () => setDetailId(null),
        editSiniestro: (id: number) => history.push(editClaimLocation(id)),
        deleted,
    }
}
